// Remote Orders Service
// Calls the documented HTTP endpoints and transforms responses
// to the internal shapes used by the UI/components.

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

function toUrl(path) {
  const base = BASE_URL.replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

async function http(method, path, { query, body } = {}) {
  const url = new URL(toUrl(path));
  if (query && typeof query === 'object') {
    Object.entries(query).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      if (Array.isArray(v)) {
        // join array params with comma, server can split if needed
        url.searchParams.set(k, v.join(','));
      } else {
        url.searchParams.set(k, String(v));
      }
    });
  }

  const res = await fetch(url.toString(), {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Ensure 2xx
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  // Some dev servers may respond HTML on missing routes; try JSON parse
  let data;
  try {
    data = await res.json();
  } catch {
    throw new Error('Invalid JSON response');
  }
  if (data && data.success === false) {
    const code = data?.error?.code || 'UNKNOWN';
    const err = new Error(`API error: ${code}`);
    err.code = code;
    throw err;
  }
  return data;
}

// =====================
// Transform helpers
// =====================
function mapOrderStatus(remote) {
  if (!remote) return 'AGUARDANDO PAGAMENTO';
  const s = String(remote).toUpperCase();
  const withSpaces = s.replace(/_/g, ' ');
  if (withSpaces === 'CONCLUIDO') return 'CONCLUÍDO';
  return withSpaces;
}

function moneyToNumber(obj) {
  if (!obj || typeof obj !== 'object') return 0;
  const int = Number(obj.int || 0);
  const dec = Number(obj.dec || 0);
  return int + dec / 100;
}

function mapPayment(p) {
  if (!p) return {
    environment: 'Presencial',
    method: 'Dinheiro',
    paidAt: null,
    amountPaid: 0,
    change: 0,
    paymentCode: null,
    receiptUrl: null,
  };
  const methodMap = {
    PIX: 'PIX',
    DINHEIRO: 'Dinheiro',
    CARTAO_CREDITO: 'Cartão Crédito',
    CARTAO_DEBITO: 'Cartão Débito',
  };
  const envMap = { APLICATIVO: 'Aplicativo', PRESENCIAL: 'Presencial' };
  return {
    environment: envMap[p.ambiente] || 'Presencial',
    method: methodMap[p.metodo] || 'Dinheiro',
    paidAt: p.data_pagamento || null,
    amountPaid: Number(p.valor_pago || 0),
    change: Number(p.troco || 0),
    paymentCode: p?.chaves?.codigo_pagamento || null,
    receiptUrl: p?.nota_fiscal_url || null,
  };
}

function mapDelivery(d) {
  if (!d) return { method: 'Retirar na loja', maxPickupDate: null };
  const methodId = d.metodo_entrega_id || '';
  let method = methodId;
  if (methodId === 'PICK_UP_IN_STORE') method = 'Retirar na loja';
  else if (methodId === 'VIA_CORREIOS') method = 'Correios';
  const maxPickupDate = d?.coleta?.data_max_coleta || null;
  return { method, maxPickupDate };
}

function mapOrderItem(it) {
  // Remote item lacks name/variant; use SKU as name placeholder
  const unitPrice = moneyToNumber(it?.resumo?.valor_unitario);
  const discount = moneyToNumber(it?.resumo?.descontos);
  return {
    id: String(it.SKU),
    name: String(it.SKU),
    variant: '',
    thumbnail: null,
    productionStatus: String(it.status || 'PENDENTE'),
    color: '',
    style: '',
    weight: '',
    type: '',
    unitPrice,
    quantity: Number(it?.resumo?.quantidade || 1),
    discountAmount: discount,
  };
}

function mapOrder(o) {
  const subtotal = moneyToNumber(o?.resumo?.subtotal);
  const shipping = moneyToNumber(o?.resumo?.entrega);
  const discounts = moneyToNumber(o?.resumo?.descontos);
  const total = moneyToNumber(o?.resumo?.total);
  const products = Array.isArray(o?.itens) ? o.itens.map(mapOrderItem) : [];
  return {
    id: String(o.id_publico),
    code: `PED-${o.id_publico}`,
    createdAt: o.data_criacao || new Date().toISOString(),
    status: mapOrderStatus(o.status),
    type: o.tipo_item || 'MERCADORIA',
    payment: mapPayment(o.pagamento),
    delivery: mapDelivery(o.informacoes_entrega),
    pricing: {
      subtotal,
      shipping,
      discounts,
      total,
    },
    products,
    // Optional fields used in UI if present
    returns: o.devolucoes || [],
    invoice: o.pagamento?.nota_fiscal_url ? { url: o.pagamento.nota_fiscal_url, filename: 'nota_fiscal.pdf' } : null,
  };
}

function simplifyOrder(order) {
  return {
    id: order.id,
    code: order.code,
    createdAt: order.createdAt,
    status: order.status,
    total: order.pricing?.total || 0,
    productCount: Array.isArray(order.products) ? order.products.length : 0,
    productThumbs: (order.products || []).slice(0, 5).map(p => ({ id: p.id, name: p.name, thumbnail: p.thumbnail }))
  };
}

// =====================
// Public API (remote)
// =====================
export async function listOrders({ statuses = [], search = '' } = {}) {
  const query = { _withDetails: 'true' }; // leverage list with items for thumbs
  if (Array.isArray(statuses) && statuses.length) {
    // server spec shows only one status; we pass comma-separated
    query.status = statuses.join(',');
  }
  const data = await http('GET', '/api/stores/orders', { query });
  const orders = Array.isArray(data?.data?.orders) ? data.data.orders.map(mapOrder) : [];
  // Client-side search by code
  const term = typeof search === 'string' ? search.trim().toLowerCase() : '';
  const filtered = term ? orders.filter(o => o.code.toLowerCase().includes(term)) : orders;
  return filtered.map(simplifyOrder);
}

export async function getOrder(orderId) {
  const data = await http('GET', `/api/stores/orders/${encodeURIComponent(orderId)}`);
  const raw = data?.data?.order;
  return raw ? mapOrder(raw) : null;
}

export async function updateItemStatus(orderId, merchandiseId, newStatus) {
  const body = { status: newStatus };
  await http('PUT', `/api/stores/orders/${encodeURIComponent(orderId)}/merchandise/${encodeURIComponent(merchandiseId)}/status`, { body });
  return true;
}

export async function cancelOrder(orderId, reason = 'Cancelado pelo vendedor') {
  const body = { requester_type: 'VENDEDOR', reason };
  await http('POST', `/api/stores/orders/${encodeURIComponent(orderId)}/cancel`, { body });
  return true;
}

const orderRemoteService = { listOrders, getOrder, updateItemStatus, cancelOrder };
export default orderRemoteService;
